import groupBy from 'lodash.groupby';
import {
  DataFieldWithDataType,
  sameKindFiltersSplit,
} from '../../../common-types';
import {
  ApiMasterDataAdvancedQuery,
  ApiMasterDataAdvancedQueryDimension,
  ApiMasterDataQuery,
  ApiMasterDataQueryMeasure,
  ApiMasterDataQueryResponse,
  ApiMasterDataQueryResponseDataPoint,
  ApiMasterDataTypes,
} from '../api/api-interfaces';
import { BenchmarkConfig } from '../api/master-data-service';
import { DataFields, DataTypes } from '../constants/constants';
import { UndefinedValue } from '../constants/systemValuesTranslation';
import {
  HierarchicalFilterValue,
  TimeSliderConfig,
} from '../filter/filter-store';
import { combineFilters } from '../filter/utils';
import { ForecastConfig } from '../forecast/forecast-store';
import { formatDimValueToChartLabel } from '../utilFunctions/formatters';
import { isHierarchical } from '../utilFunctions/utils';
import { dataPointHasMeasures } from '../utilFunctions/validators';
import { VersionId } from '../v2/api/types';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../v2/context/legacy-types';
import { ApiMasterDataQueryFilterItem } from '../v2/types';
import {
  DataMapAndLabelsWithForecast,
  getForecastResult,
} from './getForecastResult';
import {
  GetResponseForAdvancedQueryService,
  GetResponseForMasterQueryService,
} from './GetResponseForQueryService';
import { GetResponseForQueriesService } from './GetResponsesForQueriesService';
import { runServiceForDates, Service } from './utils';

type DefaultDataPointTransformerValue = number | 'CONFIDENTIAL';

type DefaultDataPointTransformer = (
  dataPoints: ApiMasterDataQueryResponseDataPoint[],
  dataKey: string
) => Record<string, DefaultDataPointTransformerValue>;

export type CustomDataPointTransformer = (
  dataPoints: ApiMasterDataQueryResponseDataPoint[],
  legacyDomainScopedStore: LegacyDomainScopedStore
) => Record<string, any>;

export interface ServiceInputs {
  service: Service<any, any>;
  inputs: Record<string, any>;
  filters: ApiMasterDataQueryFilterItem[];
}
interface ByDimensionServiceInputs {
  dimension: DataFieldWithDataType;
  baseQueryDimensions?: string[];
  measures?: ApiMasterDataQueryMeasure[];
  filters: ApiMasterDataQueryFilterItem[];
  getDataMapValueFromDataPoints?: CustomDataPointTransformer;
  dataType: ApiMasterDataTypes | DataTypes;
  dataKey?: string;
  benchmarkConfig?: BenchmarkConfig;
  convertDataPointsToDataMap?: (
    dataPoints: ApiMasterDataQueryResponseDataPoint[]
  ) => Record<string, any>;
  baseQuery?: ApiMasterDataAdvancedQuery;
  useAdvancedQuery?: boolean;
  forecastConfig?: ForecastConfig | null;
  timeSliderConfig?: TimeSliderConfig;
  serviceInputs?: ServiceInputs;
  groupByDimDataType?: DataTypes;
  // groupByDimDataType governs the datatype of group by dimension(location, org, etc)
  // employee is most common for this(eg- payroll, attrition-risk, etc)
}

const defaultTransform: DefaultDataPointTransformer = (dataPoints, dataKey) => {
  const dp = dataPoints[0];
  const value: number | 'CONFIDENTIAL' = !dataPointHasMeasures(dp)
    ? 0
    : (dp.measures[0].value ?? 0);
  return {
    [dataKey as string]: value,
  };
};

export const ByDimensionService = async (
  inputs: ByDimensionServiceInputs,
  latestVersions: Partial<Record<DataTypes, VersionId>>,
  legacyStores: LegacyStore,
  legacyDomainScopedStores: LegacyDomainScopedStore
): Promise<DataMapAndLabelsWithForecast> => {
  const {
    dimension,
    baseQueryDimensions = [],
    measures,
    filters,
    benchmarkConfig,
    getDataMapValueFromDataPoints,
    dataType,
    dataKey,
    baseQuery,
    useAdvancedQuery = false,
    forecastConfig,
    serviceInputs,
    groupByDimDataType,
  } = inputs;

  const { cachedMasterDataApiService } = legacyDomainScopedStores;

  const hasEitherBothUseAdvancedQueryAndBaseQueryOrHasNeither =
    (useAdvancedQuery && baseQuery) || !(useAdvancedQuery || baseQuery);

  if (!hasEitherBothUseAdvancedQueryAndBaseQueryOrHasNeither) {
    throw new Error(
      'baseQuery must be provided if and only if using advanced query'
    );
  }

  const limitedToPermittedPopulation =
    benchmarkConfig && benchmarkConfig.limitedToPermittedPopulation === false
      ? false
      : true;

  const [sameKindFilters, nonSameKindFilters] = sameKindFiltersSplit(
    filters,
    dimension
  );

  const rawDataMap: Record<string, any> = {};
  const labelsSet = new Set<string>(); // Don't think labels are really useful for anything so should think about just returning datasets

  const isHierarchicalDimension = isHierarchical(dimension);
  const getApiDimensionObjForHierarchicalLevel = (
    level: number
  ): DataFields => {
    const dim = isHierarchicalDimension
      ? `${dimension.dataField}_LEVEL_${level}`
      : dimension.dataField;
    const dimInput = useAdvancedQuery
      ? { dataType: groupByDimDataType ?? DataTypes.EMPLOYEE, property: dim }
      : dim;
    return dimInput as DataFields;
  };
  if (!sameKindFilters.length || !isHierarchicalDimension) {
    let query: any;
    const dimInput = getApiDimensionObjForHierarchicalLevel(1);
    if (baseQuery) {
      query = {
        ...baseQuery,
        dimensions: [dimInput, ...(baseQuery.dimensions || [])],
        filterItems: combineFilters(filters),
      };
    } else {
      query = {
        dimensions: [dimInput, ...(baseQueryDimensions || [])],
        measures,
        filterItems: combineFilters(filters),
        dataType: dataType as DataTypes,
        limitedToPermittedPopulation,
      };
    }
    const result = await (useAdvancedQuery
      ? GetResponseForAdvancedQueryService(
          query,
          cachedMasterDataApiService,
          benchmarkConfig
        )
      : GetResponseForMasterQueryService(
          query,
          cachedMasterDataApiService,
          latestVersions[dataType as DataTypes],
          benchmarkConfig
        ));
    result?.dataPoints?.forEach((dp: ApiMasterDataQueryResponseDataPoint) => {
      const label = dp.dimensions[0].value || UndefinedValue;
      labelsSet.add(label);
      if (!rawDataMap[label]) {
        rawDataMap[label] = [];
      }
      rawDataMap[label].push(dp);
    });
  } else {
    const filtersByLevel: Record<string, ApiMasterDataQueryFilterItem[]> =
      groupBy(
        sameKindFilters,
        (f: ApiMasterDataQueryFilterItem) =>
          (f.values.first() as string[]).length
      );
    const numLevelsList = Object.keys(filtersByLevel).map(Number);
    let getQueryFromFiltersAndDimInputs: (
      filters: ApiMasterDataQueryFilterItem[],
      dimInputs: Array<string | ApiMasterDataAdvancedQueryDimension>
    ) => ApiMasterDataQuery | ApiMasterDataAdvancedQuery;
    if (baseQuery) {
      getQueryFromFiltersAndDimInputs = (filters, dimInputs) => {
        return {
          ...baseQuery,
          dimensions: [
            ...(dimInputs as ApiMasterDataAdvancedQueryDimension[]),
            ...(baseQuery.dimensions || []),
          ],
          filterItems: combineFilters([...nonSameKindFilters, ...filters]),
          limitedToPermittedPopulation,
        };
      };
    } else {
      getQueryFromFiltersAndDimInputs = (filters, dimInputs) => {
        return {
          dimensions: [...(dimInputs as string[]), ...baseQueryDimensions],
          measures,
          filterItems: combineFilters([...nonSameKindFilters, ...filters]),
          dataType,
          limitedToPermittedPopulation,
        } as ApiMasterDataQuery;
      };
    }
    const queries: (ApiMasterDataQuery | ApiMasterDataAdvancedQuery)[] =
      Object.entries(filtersByLevel).map(([level, filters]) => {
        const dimInputs = Array(Number(level))
          .fill(0)
          .map((_, i) => getApiDimensionObjForHierarchicalLevel(Number(i + 1)));
        const query = getQueryFromFiltersAndDimInputs(filters, dimInputs);
        return query;
      });

    let responses: ApiMasterDataQueryResponse[];
    if (useAdvancedQuery) {
      responses = await Promise.all(
        (queries as ApiMasterDataAdvancedQuery[]).map((q) =>
          GetResponseForAdvancedQueryService(
            q,
            cachedMasterDataApiService,
            benchmarkConfig
          )
        )
      );
    } else {
      const result = await GetResponseForQueriesService(
        queries as ApiMasterDataQuery[],
        latestVersions,
        cachedMasterDataApiService,
        benchmarkConfig
      );
      responses = result?.responseListItems.map((r) => r.response) ?? [];
    }

    responses.forEach((response, index) => {
      response?.dataPoints?.forEach((dp) => {
        const numLevels = numLevelsList[index];
        const filterValue: HierarchicalFilterValue = [];
        for (let i = 0; i < numLevels; i++) {
          filterValue.push(dp.dimensions[i].value ?? null);
        }
        const label = formatDimValueToChartLabel(filterValue, dimension);
        labelsSet.add(label);
        if (!rawDataMap[label]) {
          rawDataMap[label] = [];
        }
        rawDataMap[label].push(dp);
      });
    });
  }
  const dataMap: Record<string, any> = {};
  Object.keys(rawDataMap).forEach((key) => {
    const dataPoints = rawDataMap[key];
    dataMap[key] = getDataMapValueFromDataPoints
      ? getDataMapValueFromDataPoints(dataPoints, legacyDomainScopedStores)
      : defaultTransform(dataPoints, dataKey as string);
  });
  const labels = Array.from(labelsSet).filter(
    (l) => dataMap[l][dataKey as string] !== 0
  );

  //forecast stuff

  if (forecastConfig) {
    const forecastTimeOption = forecastConfig?.forecastTimeOption;
    const timeSliderConfig = inputs.timeSliderConfig as TimeSliderConfig;
    const forecastEndDate = forecastTimeOption.getForecastTillDate(
      timeSliderConfig.endDate
    );
    const getActualValueForDates = async (sDate: string, eDate: string) => {
      return (
        await runServiceForDates(
          sDate,
          eDate,
          serviceInputs as ServiceInputs,
          timeSliderConfig,
          latestVersions,
          legacyStores,
          legacyDomainScopedStores
        )
      ).dataMap;
    };
    const forecastResponse: any = await getForecastResult({
      timeSliderConfig,
      actualValue: dataMap,
      forecastEndDate,
      getActualValueForDates,
    });
    return { labels, dataMap, forecastDataMap: forecastResponse };
  }

  return { labels, dataMap };
};
